import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Carousel } from 'react-bootstrap';

/*
  Class which defines a custom carousel of customer testimonials
  built using bootstrap's carousel and containing a panel of interactive logos.

  The content of the carousel has been defined in EmployerSignupPanel.jsx and
  is passed in as a prop (the testimonials), where
  this component is used to construct the employer registration page.

  The autoplay interval (the duration that needs to pass for the carousel to
  automatically cycle to the next panel) is also specified as a prop.
  This number is in milliseconds.
*/
class EmployerTestimonialCarousel extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentActiveIndex: 0,
      scheduledIncrements: [],
    };

    this.clearScheduledIncrements = this.clearScheduledIncrements.bind(this);
    this.setCurrentActiveIndex = this.setCurrentActiveIndex.bind(this);
    this.renderLogoPanel = this.renderLogoPanel.bind(this);
    this.renderCarousel = this.renderCarousel.bind(this);
    this.incrementActiveIndex = this.incrementActiveIndex.bind(this);

    const newAutoplayEvent = window.setTimeout(this.incrementActiveIndex, this.props.interval);
    this.state.scheduledIncrements.push(newAutoplayEvent);
  }

  setCurrentActiveIndex(newActiveIndex) {
    const { interval, testimonials } = this.props;

    if (newActiveIndex < 0 || newActiveIndex >= testimonials.length) {
      return;
    }

    this.setState({ currentActiveIndex: newActiveIndex });
    const newAutoplayEvent = window.setTimeout(this.incrementActiveIndex, interval);
    this.state.scheduledIncrements.push(newAutoplayEvent);
  }

  clearScheduledIncrements() {
    this.state.scheduledIncrements.map((increment) => window.clearTimeout(increment));
    this.state.scheduledIncrements.slice(0, this.state.scheduledIncrements.length);
  }

  incrementActiveIndex() {
    const { testimonials } = this.props;
    const activeIndex = this.state.currentActiveIndex;
    const newActiveIndex = (activeIndex === testimonials.length - 1 ? 0 : activeIndex + 1);

    // Notice that this call will schedule a timeout event 15 s from now.
    // This is 100% intentional and creates the infinite autoplay
    // effect for the carousel.
    this.setCurrentActiveIndex(newActiveIndex);
  }

  renderLogoPanel() {
    const { testimonials } = this.props;

    return (
      <div className="logo-panel">
        {testimonials.map((testimony, index) => (
          <div
            role="button"
            tabIndex={index}
            onClick={() => {
              this.clearScheduledIncrements();
              this.setCurrentActiveIndex(index);
            }}
          >
            <img
              className="logo"
              src={(index === this.state.currentActiveIndex)
                ? testimony.activeLogo : testimony.inactiveLogo}
              alt="Company Logo"
              width={testimony.logoWidth}
              height={testimony.logoHeight}
            />
          </div>
        )) }
      </div>
    );
  }

  renderCarousel() {
    const { testimonials } = this.props;

    return (
      <Carousel
        activeIndex={this.state.currentActiveIndex}
        controls={false}
        touch
        onSelect={(carouselPageIndex) => {
          this.clearScheduledIncrements();
          this.setCurrentActiveIndex(carouselPageIndex);
        }}
      >
        {testimonials.map((testimony) => (
          <Carousel.Item>
            <div className="visible-750-1k">
              <div className="logo">
                <img
                  src={testimony.activeLogo}
                  alt="Company Logo"
                />
              </div>
            </div>
            <div className="body-text-2 employer-testimonial-blurb">
              &#8220;
              {testimony.body}
              &#8221;
            </div>
            <div className="employer-testimonial-byline">
              <div className="body-text-2 employer-name">
                {testimony.managerName}
              </div>
              <div className="body-text-2 employer-title">
                {testimony.managerTitle}
              </div>
            </div>
          </Carousel.Item>
        )) }
      </Carousel>
    );
  }

  render() {
    return (
      <div className="employer-testimonial-carousel">
        {this.renderLogoPanel()}
        {this.renderCarousel()}
      </div>
    );
  }
}

EmployerTestimonialCarousel.propTypes = {
  testimonials: PropTypes.arrayOf(PropTypes.shape({
    managerName: PropTypes.string.isRequired,
    managerTitle: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    activeLogo: PropTypes.string.isRequired,
    inactiveLogo: PropTypes.string.isRequired,
    logoWidth: PropTypes.number.isRequired,
    logoHeight: PropTypes.number.isRequired,
  })),
  interval: PropTypes.number,
};

EmployerTestimonialCarousel.defaultProps = {
  testimonials: [],
  interval: 15000,
};

export default EmployerTestimonialCarousel;
